import { Spacing } from '@persona-id/design-tokens';
import { LinkExternalIcon } from '@persona-id/design-tokens/icons';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import BlueprintsAllModal from 'dashboard/components/BlueprintsAllModal';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { Account } from 'dashboard/models/account';
import {
  Blueprint,
  BlueprintPreviewMap,
  BlueprintServerMetadata,
} from 'dashboard/models/blueprint';
import { Case } from 'dashboard/models/case';
import { CaseAnalyticsStatusDistributionCurrentUser } from 'dashboard/models/case-analytics';
import { CaseQueue } from 'dashboard/models/case-queue';
import { CaseTemplateBasicsOption } from 'dashboard/models/case-template';
import { Inquiry } from 'dashboard/models/inquiry';
import { BuilderPreviewMap } from 'dashboard/models/inquiry-template';
import { MetricsInquiryVolume } from 'dashboard/models/metrics';
import { Organization, ProductFeatureName } from 'dashboard/models/organization';
import { OrganizationPlan } from 'dashboard/models/organization-plan';
import { Tag } from 'dashboard/models/tag';
import { User } from 'dashboard/models/user';
import DashboardHomeCasesActivity from 'dashboard/views/DashboardHomeView/DashboardHomeCasesActivity';
import DashboardHomeInquiriesActivity from 'dashboard/views/DashboardHomeView/DashboardHomeInquiriesActivity';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import TabBar, { Tab } from 'design-system/components/TabBar';
import { Heading, Paragraph } from 'design-system/components/Text';
import useProductFeature from 'lib/hooks/useProductFeature';

export interface DashboardHomeViewProps {
  cases: Case[];
  caseStatusDistributionForCurrentUser: CaseAnalyticsStatusDistributionCurrentUser;
  caseUsers: User[];
  caseCaseQueues: CaseQueue[];
  caseTags: Tag[];
  caseAccounts: Account[];
  caseStates: { states: CaseTemplateBasicsOption['states']; initialStateToken: string }[];
  currentOrganizationPlan: OrganizationPlan;
  isCasesLoading: boolean;
  isCaseAnalyticsLoading: boolean;
  isCaseStatesLoading: boolean;
  isCaseTagsLoading: boolean;
  inquiries: Inquiry[];
  inquiryAnalyticsVolume: MetricsInquiryVolume;
  inquiryAnalyticsVolumeDelta: MetricsInquiryVolume;
  isInquiriesLoading: boolean;
  isInquiryAnalyticsLoading: boolean;
  previouslyUsedBlueprints: Blueprint[];
  blueprintsCatalogServer: BlueprintServerMetadata[];
  blueprintPreviews: BlueprintPreviewMap;
  inquiryTemplateBuilderPreviews: BuilderPreviewMap;
  organization: Organization;
  createBlueprint: (blueprintKey: string) => void;
  handleLoadBlueprintPreview: (blueprintKey: string) => Promise<void>;
  handleLoadInquiryAnalytics: () => void;
  handleLoadInquiryTemplateBuilderPreview: (builderName: string) => Promise<void>;
}

const DashboardHomeView = ({
  cases,
  caseStatusDistributionForCurrentUser,
  caseCaseQueues,
  caseUsers,
  caseTags,
  caseAccounts,
  caseStates,
  currentOrganizationPlan,
  isCasesLoading,
  isCaseAnalyticsLoading,
  isCaseStatesLoading,
  isCaseTagsLoading,
  inquiries,
  inquiryAnalyticsVolume,
  inquiryAnalyticsVolumeDelta,
  isInquiriesLoading,
  isInquiryAnalyticsLoading,
  previouslyUsedBlueprints,
  blueprintsCatalogServer,
  blueprintPreviews,
  inquiryTemplateBuilderPreviews,
  organization,
  createBlueprint,
  handleLoadBlueprintPreview,
  handleLoadInquiryAnalytics,
  handleLoadInquiryTemplateBuilderPreview,
}: DashboardHomeViewProps) => {
  const hasCasesProductFeature = useProductFeature(ProductFeatureName.CasesDashboard);
  const [selectedTableTab, setSelectedTableTab] = useState(
    hasCasesProductFeature ? 'cases' : 'inquiries',
  );
  const [showBlueprintsAllModal, setShowBlueprintsAllModal] = useState(false);

  return (
    <>
      <ModalWrapper isOpen={showBlueprintsAllModal}>
        <BlueprintsAllModal
          blueprintsCatalogServer={blueprintsCatalogServer}
          blueprintPreviews={blueprintPreviews}
          inquiryTemplateBuilderPreviews={inquiryTemplateBuilderPreviews}
          previouslyUsedBlueprints={previouslyUsedBlueprints}
          organization={organization}
          currentOrganizationPlan={currentOrganizationPlan}
          handleLoadBlueprintPreview={handleLoadBlueprintPreview}
          handleLoadInquiryTemplateBuilderPreview={handleLoadInquiryTemplateBuilderPreview}
          createBlueprint={createBlueprint}
          closeModal={() => setShowBlueprintsAllModal(false)}
        />
      </ModalWrapper>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}>
        <Container>
          <Header title='Home' data-test='home__header' />
          <Section>
            <LayoutWrapper>
              <WideCards>
                <Card>
                  <Card.Body>
                    <Card.Title>Recent Activity</Card.Title>
                    <TabBar>
                      {hasCasesProductFeature && (
                        <Tab
                          selected={selectedTableTab === 'cases'}
                          onClick={() => setSelectedTableTab('cases')}
                        >
                          Cases
                        </Tab>
                      )}
                      <Tab
                        selected={selectedTableTab === 'inquiries'}
                        onClick={() => setSelectedTableTab('inquiries')}
                      >
                        Inquiries
                      </Tab>
                    </TabBar>
                    <Spacer height={1} />
                    {selectedTableTab === 'cases' && (
                      <DashboardHomeCasesActivity
                        cases={cases}
                        caseStatusDistributionForCurrentUser={caseStatusDistributionForCurrentUser}
                        caseCaseQueues={caseCaseQueues}
                        caseUsers={caseUsers}
                        caseStates={caseStates}
                        caseTags={caseTags}
                        caseAccounts={caseAccounts}
                        isCasesLoading={isCasesLoading}
                        isCaseAnalyticsLoading={isCaseAnalyticsLoading}
                        isCaseStatesLoading={isCaseStatesLoading}
                        isCaseTagsLoading={isCaseTagsLoading}
                      />
                    )}
                    {selectedTableTab === 'inquiries' && (
                      <DashboardHomeInquiriesActivity
                        inquiries={inquiries}
                        inquiryAnalyticsVolume={inquiryAnalyticsVolume}
                        inquiryAnalyticsVolumeDelta={inquiryAnalyticsVolumeDelta}
                        isInquiriesLoading={isInquiriesLoading}
                        isInquiryAnalyticsLoading={isInquiryAnalyticsLoading}
                        handleLoadInquiryAnalytics={handleLoadInquiryAnalytics}
                      />
                    )}
                  </Card.Body>
                </Card>
              </WideCards>
              <NarrowCards>
                <Card>
                  <Card.Body>
                    <NarrowCardHeader>
                      <Heading size='sm'>Additional resources</Heading>
                    </NarrowCardHeader>
                    <Link to='/dashboard/getting-started'>Getting Started</Link>
                    <a href='https://help.withpersona.com' target='_blank' rel='noreferrer'>
                      Help Center <LinkExternalIcon />
                    </a>
                    <a href='https://docs.withpersona.com/docs' target='_blank' rel='noreferrer'>
                      Developer documentation <LinkExternalIcon />
                    </a>
                    <a href='https://academy.withpersona.com' target='_blank' rel='noreferrer'>
                      Persona Academy <LinkExternalIcon />
                    </a>
                  </Card.Body>
                </Card>
                <Card>
                  <Card.Body>
                    <NarrowCardHeader>
                      <Heading size='sm'>Explore all solutions</Heading>
                    </NarrowCardHeader>
                    <Paragraph>
                      Browse all of our pre-built solutions that use our most popular building
                      blocks.
                    </Paragraph>
                    <Button
                      id='home__all-blueprints'
                      variant='basic'
                      width='button'
                      onClick={() => setShowBlueprintsAllModal(true)}
                    >
                      Browse solutions
                    </Button>
                  </Card.Body>
                </Card>
                <Card>
                  <Card.Body>
                    <NarrowCardHeader>
                      <Heading size='sm'>Invite your teammates</Heading>
                    </NarrowCardHeader>
                    <Paragraph>
                      Invite your team members or coworkers to this Persona organization and
                      provision what they can view and act on via role assignments.
                    </Paragraph>
                    <Spacer height={1} />
                    <Link to='/dashboard/users'>
                      <Button variant='basic' width='button'>
                        Invite users
                      </Button>
                    </Link>
                  </Card.Body>
                </Card>
              </NarrowCards>
            </LayoutWrapper>
          </Section>
        </Container>
      </motion.div>
    </>
  );
};

const LayoutWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  column-gap: ${Spacing[5]};
`;

const WideCards = styled('div')`
  flex: 2;
  min-width: 800px;
`;

const NarrowCards = styled('div')`
  flex: 1;
  min-width: 300px;
`;

const NarrowCardHeader = styled('div')`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[2]};
  align-items: center;
  padding-bottom: ${Spacing[1]};
  border-bottom: 2px solid ${({ theme }) => theme.color.border.primary};
`;

export default DashboardHomeView;
